import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Card, Container } from "react-bootstrap";
import DiscussionBoardService, {
  DiscussionBoardBean,
  extractErrorMessage,
} from "../../_services/DiscussionBoardService";
import StudyResourceService from "../../_services/StudyResourceService";

// Board icons are stored as S3 https URLs (study_discussion_board.Icon). The live
// bucket isn't public, so the raw URL won't load in an <img> — sign it first.
function isS3Url(url?: string | null): boolean {
  return !!url && url.includes(".s3.") && url.includes("amazonaws.com");
}

/**
 * Staff/instructor landing page — lists discussion boards the user has access to.
 * If only one board is returned, navigate straight into its topic list.
 *
 * Board creation lives in the Study Resource form (typeId = 20). The dual-write to
 * study_resource_master + study_discussion_board still happens server-side; this page
 * is read-only.
 */
const DiscussionBoardListPage: React.FC = () => {
  const history = useHistory();
  const [boards, setBoards] = useState<DiscussionBoardBean[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // board id → signed icon URL (only for S3-hosted icons)
  const [signedIcons, setSignedIcons] = useState<Record<string, string>>({});

  const reload = useCallback(() => {
    setLoading(true);
    DiscussionBoardService.listBoards()
      .then((res) => {
        const data = res.data || [];
        setBoards(data);
        if (data.length === 1) {
          history.replace(`/discussion-board/${data[0]!.id}`);
        }
      })
      .catch((e) => setError(extractErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [history]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Sign S3 icon URLs so the card images load; external (non-S3) icons are used as-is.
  useEffect(() => {
    const toSign = boards.filter((b) => isS3Url(b.icon) && !signedIcons[b.id]);
    if (toSign.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        toSign.map(async (b) => {
          try {
            const signed = await StudyResourceService.getSignedUrl(b.icon!);
            return [b.id, signed] as const;
          } catch {
            return [b.id, b.icon!] as const;
          }
        }),
      );
      if (!cancelled) {
        setSignedIcons((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [boards, signedIcons]);

  if (loading) return <Container className="p-4">Loading…</Container>;

  return (
    <Container fluid className="p-4">
      <div className="d-flex align-items-center mb-3">
        <h4 className="mb-0">Discussion Boards</h4>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {boards.length === 0 ? (
        <div className="text-muted">
          No discussion boards available. Create one from{" "}
          <a href="/study-resources">Study Resources</a> (choose <strong>Discussion Board</strong> as the type).
        </div>
      ) : (
        <div className="d-flex flex-wrap" style={{ gap: 16 }}>
          {boards.map((b) => {
            // S3 icons need a signed URL; while it's resolving, fall back to the placeholder.
            const iconSrc = isS3Url(b.icon) ? signedIcons[b.id] : b.icon;
            return (
            <Card
              key={b.id}
              onClick={() => history.push(`/discussion-board/${b.id}`)}
              style={{ width: 260, cursor: "pointer" }}
              className="shadow-sm"
            >
              {iconSrc ? (
                <Card.Img variant="top" src={iconSrc} style={{ height: 120, objectFit: "cover" }} />
              ) : (
                <div
                  style={{
                    height: 120,
                    background: "var(--gradient-brand)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--text-on-brand)",
                    fontSize: 32,
                  }}
                >
                  💬
                </div>
              )}
              <Card.Body>
                <Card.Title style={{ fontSize: 14, minHeight: 36 }}>{b.name}</Card.Title>
                {b.courseName && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{b.courseName}</div>}
                {b.programName && <div style={{ fontSize: 12, color: "var(--text-faint)" }}>{b.programName}</div>}
              </Card.Body>
            </Card>
            );
          })}
        </div>
      )}
    </Container>
  );
};

export default DiscussionBoardListPage;
